import { ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { BrButton } from '@govbr-ds/webcomponents-angular/standalone';
import { forkJoin, Observable } from 'rxjs';
import { ConfirmacaoVagaService } from '../../../shared/services/confirmacao-vaga.service';
import { LoadingService } from '../../../shared/services/loading.service';
import { AlertService } from '../../../shared/services/alert.service';
import { ResumoConfirmacaoPrint } from '../steps/resumo-confirmacao/resumo-confirmacao-print/resumo-confirmacao-print.component';
import {
  ACAO_HISTORICO_LABEL,
  ComprovanteConfirmacao,
  ComprovanteConteudo,
  EscopoHistoricoNacional,
  HistoricoConfirmacaoVaga,
  TIPO_COMPROVANTE_LABEL,
} from './historico-confirmacao-vaga.model';

/**
 * Modal com o histórico da confirmação de vagas e do cadastro reserva de um município ou UF
 * no ciclo (PGPP-756): comprovantes emitidos a cada efetivação (reimprimíveis) e as alterações
 * campo a campo, com quem, quando e de onde.
 *
 * Sem `escopoNacional`, consulta o escopo do gestor logado (município ou UF do perfil). Com
 * `escopoNacional`, usa os endpoints do relatório nacional (RELATORIO_CONFIRMACAO_VAGA_VISUALIZAR).
 */
@Component({
  selector: 'app-historico-confirmacao-vaga',
  standalone: true,
  imports: [CommonModule, BrButton],
  templateUrl: './historico-confirmacao-vaga.component.html',
  styleUrl: './historico-confirmacao-vaga.component.scss',
})
export class HistoricoConfirmacaoVagaComponent implements OnInit {
  @Input() modalRef!: NgbModalRef;
  /** Ciclo consultado; `numeroCiclo`/`anoCiclo` só para o título. */
  @Input() cicloSelected!: { id: number; numeroCiclo?: number; anoCiclo?: number };
  /** Texto do ciclo no título quando `cicloSelected` não traz número/ano (consulta nacional). */
  @Input() cicloDescricao: string | null = null;
  /** Texto do escopo no título (ex.: "SAO FRANCISCO DE ITABAPOANA/RJ" ou "UF RJ"). */
  @Input() escopoDescricao: string | null = null;
  @Input() escopoNacional: EscopoHistoricoNacional | null = null;

  private readonly confirmacaoVagaService = inject(ConfirmacaoVagaService);
  private readonly loadingService = inject(LoadingService);
  private readonly alertService = inject(AlertService);
  private readonly modalService = inject(NgbModal);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly tipoLabel = TIPO_COMPROVANTE_LABEL;
  readonly acaoLabel = ACAO_HISTORICO_LABEL;

  comprovantes: ComprovanteConfirmacao[] = [];
  historico: HistoricoConfirmacaoVaga[] = [];
  carregado = false;

  ngOnInit(): void {
    this.carregar();
  }

  get tituloCiclo(): string {
    const ciclo = this.cicloSelected;
    return ciclo?.numeroCiclo ? `${('00' + ciclo.numeroCiclo).slice(-3)}/${ciclo.anoCiclo}` : (this.cicloDescricao ?? '-');
  }

  fechar(): void {
    this.modalRef.dismiss();
  }

  imprimir(comprovante: ComprovanteConfirmacao): void {
    this.loadingService.open();
    const consulta$ = this.escopoNacional
      ? this.confirmacaoVagaService.fetchComprovanteNacional(comprovante.id)
      : this.confirmacaoVagaService.fetchComprovante(comprovante.id);
    consulta$.subscribe({
      next: (resposta) => {
        this.loadingService.close();
        const conteudo = JSON.parse(resposta.conteudo ?? '{}') as ComprovanteConteudo;
        const modalRef = this.modalService.open(ResumoConfirmacaoPrint, { size: 'lg' });
        modalRef.componentInstance.modalRef = modalRef;
        modalRef.componentInstance.comprovante = conteudo;
      },
      error: (error) => {
        this.loadingService.close();
        this.alertService.error(error?.error?.detail || 'Erro ao carregar o comprovante. Tente novamente.');
      },
    });
  }

  private consultas(): {
    comprovantes: Observable<ComprovanteConfirmacao[]>;
    historico: Observable<HistoricoConfirmacaoVaga[]>;
  } {
    if (this.escopoNacional) {
      return {
        comprovantes: this.confirmacaoVagaService.fetchComprovantesNacional(this.escopoNacional),
        historico: this.confirmacaoVagaService.fetchHistoricoNacional(this.escopoNacional),
      };
    }
    return {
      comprovantes: this.confirmacaoVagaService.fetchComprovantes(this.cicloSelected.id),
      historico: this.confirmacaoVagaService.fetchHistorico(this.cicloSelected.id),
    };
  }

  private carregar(): void {
    this.loadingService.open();
    forkJoin(this.consultas()).subscribe({
      next: ({ comprovantes, historico }) => {
        this.loadingService.close();
        this.comprovantes = comprovantes ?? [];
        this.historico = historico ?? [];
        this.carregado = true;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.loadingService.close();
        this.carregado = true;
        this.alertService.error(error?.error?.detail || 'Erro ao carregar o histórico. Tente novamente.');
        this.cdr.detectChanges();
      },
    });
  }
}
